import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { usePostHog } from 'posthog-js/react';
import TriEventsLoadingScreen from '@/components/common/TriEventsLoadingScreen';
import Footer from '@/components/common/Footer';

import ScrollableDescription from '@/components/common/ScrollableDescription';

// Shadcn components
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Circle } from 'lucide-react';
import { CheckoutMetadata } from '@/types/checkout/checkout';
import { createCheckoutSession } from '@/services/checkout/createCheckoutSession';
import { getTicketsAvailableForPrice } from '@/services/ticket/getTicketsAvailable';
import { useEventData } from '@/hooks/checkout/useEventData';
import { useBursaryState } from '@/hooks/checkout/useBursaryState';
import { useEmailValidation, usePhoneValidation } from '@/hooks/common/useInputValidation';
// import { useBursarySystemFlag } from '@/hooks/common/useFeatureFlag';

// Common Components
import { DisabledFeatureMessage } from "@/components/common/DisabledFeatureMessage";

const CheckoutPage = () => {
    const { uuid } = useParams<{ uuid: string }>();
    const navigate = useNavigate();
    const posthog = usePostHog();

    const { event, prices, eventTicketsLeft, loading } = useEventData(uuid, navigate);
    const [selectedOption, setSelectedOption] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [showTermsDialog, setShowTermsDialog] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);

    // Track event page view when event data is loaded
    useEffect(() => {
        if (event && posthog && !loading) {
            posthog.capture('event_page_viewed', {
                event_name: event.title,
                event_uuid: event.uuid,
                organization_uuid: event.org_uuid,
                ticket_prices_available: prices.length,
                event_date: event.date,
                event_location: event.location,
                tickets_remaining: eventTicketsLeft
            });
        }
    }, [event, posthog, loading, prices.length, eventTicketsLeft]);

    const {
        requestBursary,
        setRequestBursary,
        bursaryMessage,
        setBursaryMessage,
        bursaryAmount,
        bursaryError,
        setBursaryError,
        selectedBursaryOption,
        handleBursaryAmountChange,
        handleBursaryOptionSelect,
        handleBursaryMessageChange,
        resetBursaryState
    } = useBursaryState(event, prices, selectedOption);

    // Email validation hook
    const { emailError, validateEmail, setEmailError, clearEmailError } = useEmailValidation();

    // Phone validation hook
    const { phoneError, validatePhone, filterPhoneInput, setPhoneError, clearPhoneError } = usePhoneValidation();

    // Handle email change with validation
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newEmail = e.target.value;
        setEmail(newEmail);
        
        if (newEmail) {
            const error = validateEmail(newEmail);
            setEmailError(error || '');
        } else {
            clearEmailError();
        }
    };

    // Handle phone change with validation
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPhone = filterPhoneInput(e.target.value); // Filter input to only allow valid characters
        setPhone(newPhone);
        
        if (newPhone) {
            const error = validatePhone(newPhone);
            setPhoneError(error || '');
        } else {
            clearPhoneError();
        }
    };

    const handleCheckout = async () => {
        if (!event || !uuid) return;

        // Validate email format
        const emailValidationError = validateEmail(email);
        if (emailValidationError) {
            setEmailError(emailValidationError);
            return;
        }

        // Validate phone number
        const phoneValidationError = validatePhone(phone);
        if (phoneValidationError) {
            setPhoneError(phoneValidationError);
            return;
        }

        if (requestBursary && bursaryError) return;

        if (requestBursary) {
            if (event.bursary_type === 'fixed' && !selectedBursaryOption) {
                setBursaryError('Please select a bursary option');
                return;
            }
            if (!bursaryMessage.trim()) {
                setBursaryError('Please provide a reason for your bursary request');
                return;
            }
        }

        // Add a small delay to ensure any loading screens are fully hidden before showing the dialog
        setTimeout(() => {
            setShowTermsDialog(true);
        }, 150);
    };

    const handleProceedToPayment = async () => {
        if (!agreedToTerms) {
            alert("Please agree to the Terms of Service and Privacy Policy to continue.");
            return;
        }

        if (!event || !uuid) return;

        try {
            // Verify ticket availability right before checkout
            const selectedPrice = prices.find(p => p.stripe_price_id === selectedOption);
            if (!selectedPrice) {
                alert("Selected ticket type not found. Please try again.");
                return;
            }

            const currentTicketsAvailable = await getTicketsAvailableForPrice(selectedPrice.uuid);
            if (currentTicketsAvailable <= 0) {
                alert("Sorry, this ticket type is no longer available. Please refresh the page to see current availability.");
                return;
            }

            const metadata: CheckoutMetadata = {
                event_uuid: uuid,
                organization_uuid: event.org_uuid,
                stripe_product_id: event.stripe_product_id,
                stripe_price_id: selectedOption,
                ticket_name: name,
                ticket_email: email,
                ticket_phone: phone,
                request_bursary: requestBursary ? true : undefined,
                bursary_amount: requestBursary ? Math.round(parseFloat(bursaryAmount) * 100) : null,
                bursary_message: requestBursary ? bursaryMessage : null
            };

            const session = await createCheckoutSession(selectedOption, metadata);
            
            // Track checkout initiation
            if (posthog && event) {
                const selectedPrice = prices.find(p => p.stripe_price_id === selectedOption);
                posthog.capture('checkout_initiated', {
                    event_name: event.title,
                    event_uuid: event.uuid,
                    organization_uuid: event.org_uuid,
                    ticket_type: selectedPrice?.nickname || 'Unknown',
                    ticket_price: selectedPrice ? (selectedPrice.unit_amount / 100) : 0,
                    request_bursary: requestBursary,
                    bursary_amount: requestBursary ? parseFloat(bursaryAmount) : null
                });
            }
            
            if (session.url) {
                window.location.href = session.url;
            } else {
                alert("There was an error redirecting to the checkout page. Please try again.");
            }
        } catch (error) {
            alert(`Error: ${error instanceof Error ? error.message : "There was an error creating the checkout session. Please try again."}`);
        }
    };

    const handleOptionChange = (priceId: string) => {
        setSelectedOption(priceId);
        
        // Track ticket selection
        if (posthog && event) {
            const selectedPrice = prices.find(p => p.stripe_price_id === priceId);
            posthog.capture('ticket_type_selected', {
                event_name: event.title,
                event_uuid: event.uuid,
                organization_uuid: event.org_uuid,
                ticket_type: selectedPrice?.nickname || 'Unknown',
                ticket_price: selectedPrice ? (selectedPrice.unit_amount / 100) : 0
            });
        }
        
        if (requestBursary) {
            resetBursaryState();
        }
    };

    if (!event || loading) {
        return <TriEventsLoadingScreen />;
    }

    // if (!checkoutEnabled) {
    //     return (
    //         <div className="relative min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 overflow-x-hidden flex items-center justify-center">
    //             <div className="container mx-auto px-4 py-12 relative z-10">
    //                 <Card className="w-full max-w-2xl mx-auto shadow-2xl border-0 bg-white/95 backdrop-blur-md">
    //                     <CardHeader className="text-center space-y-6 py-12">
    //                         <div className="flex justify-center mb-4">
    //                             <img src="/branding/TriEvents_Logo.png" alt="TriEvents" className="h-8 md:h-12" />
    //                         </div>
    //                         <CardTitle className="text-3xl font-bold text-blue-900">TriEvents Checkout Temporarily Disabled</CardTitle>
    //                         <CardDescription className="text-lg text-gray-600">
    //                             Our payment processing system is currently unavailable.
    //                         </CardDescription>
    //                     </CardHeader>
    //                     <CardContent className="px-8 pb-8">
    //                         <div className="space-y-6">
    //                             <DisabledFeatureMessage 
    //                                 featureName="TriEvents checkout"
    //                                 message="Our payment processing system is temporarily disabled. This affects all events on TriEvents, not just this specific event."
    //                             />
    //                             <div className="text-center text-sm text-gray-600">
    //                                 <p className="mb-4">This is a platform-wide issue and not specific to this event.</p>
    //                                 <p>Please try again later or contact TriEvents support for assistance.</p>
    //                             </div>
    //                         </div>
    //                     </CardContent>
    //                     <CardFooter className="flex flex-col items-center gap-6 pb-12">
    //                         <div className="text-sm text-gray-500 text-center w-full">
    //                             Need assistance? Contact TriEvents Support:
    //                         </div>
    //                         <div className="flex flex-col items-center gap-3 text-sm">
    //                             <div className="flex items-center gap-2">
    //                                 <span className="text-gray-600 font-medium">Email:</span>
    //                                 <a href="mailto:support@tricode.cloud" className="text-blue-600 hover:text-teal-600 font-medium">
    //                                     support@tricode.cloud
    //                                 </a>
    //                             </div>
    //                         </div>
    //                     </CardFooter>
    //                 </Card>
    //             </div>
    //         </div>
    //     );
    // }

    if (!event.ticket_sales_active) {
        return (
            <div className="relative min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 overflow-x-hidden flex items-center justify-center">
                <div className="container mx-auto px-4 py-12 relative z-10">
                    <Card className="w-full max-w-2xl mx-auto shadow-2xl border-0 bg-white/95 backdrop-blur-md">
                        <CardHeader className="text-center space-y-6 py-12">
                            <div className="flex justify-center mb-4">
                                <img src="/branding/TriEvents_Logo.png" alt="TriEvents" className="h-8 md:h-12" />
                            </div>
                            <CardTitle className="text-3xl font-bold text-blue-900">Ticket Sales Disabled</CardTitle>
                            <CardDescription className="text-lg text-gray-600">
                                Ticket sales for this event are currently not available.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="px-8 pb-8">
                            <Alert variant="destructive" className="text-center relative py-8">
                                <AlertDescription className="text-center text-base">
                                    The event organizer has temporarily disabled ticket sales for this event. 
                                    This does not affect tickets that have already been purchased.
                                </AlertDescription>
                            </Alert>
                        </CardContent>
                        <CardFooter className="flex flex-col items-center gap-6 pb-12">
                            <div className="text-sm text-gray-500 text-center w-full">
                                Need assistance? Contact the event organizer:
                            </div>
                            <div className="flex flex-col items-center gap-3 text-sm">
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-600 font-medium">Email:</span>
                                    <a href={`mailto:${event.contact_email}`} className="text-blue-600 hover:text-teal-600 font-medium">
                                        {event.contact_email}
                                    </a>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-600 font-medium">Phone:</span>
                                    <a href={`tel:${event.contact_number}`} className="text-blue-600 hover:text-teal-600 font-medium">
                                        {event.contact_number}
                                    </a>
                                </div>
                            </div>
                        </CardFooter>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="relative min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 overflow-x-hidden">
            <HelmetProvider>
                <Helmet>
                    <title>{event.title}</title>
                    <meta name="description" content={event.description} data-react-helmet="true" />
                    
                    {/* Open Graph / Facebook */}
                    <meta property="og:type" content="website" data-react-helmet="true" />
                    <meta property="og:title" content={event.title} data-react-helmet="true" />
                    <meta property="og:description" content={event.description} data-react-helmet="true" />
                    <meta property="og:image" content="" data-react-helmet="true" />
                    <meta property="og:image:width" content="1200" data-react-helmet="true" />
                    <meta property="og:image:height" content="630" data-react-helmet="true" />
                    
                    {/* Twitter */}
                    <meta property="twitter:card" content="summary_large_image" data-react-helmet="true" />
                    <meta property="twitter:title" content={event.title} data-react-helmet="true" />
                    <meta property="twitter:description" content={event.description} data-react-helmet="true" />
                    <meta property="twitter:image" content="" data-react-helmet="true" />
                </Helmet>
            </HelmetProvider>

            <div className="container mx-auto px-4 md:px-8 lg:px-12 pt-6 md:pt-24 pb-8 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-[2fr_2.5fr] gap-8 xl:gap-20 min-h-[80vh]">
                    {/* Left Column - Event Details */}
                    <div className="flex flex-col">
                        <Card className="w-full shadow-xl border-0 bg-white/90 backdrop-blur-md p-0">
                            <CardHeader className="p-0">
                                {/* Placeholder for event image */}
                                <div className="relative h-64 w-full flex items-center justify-center bg-gradient-to-tr from-blue-100 via-blue-50 to-teal-100 rounded-t-2xl overflow-hidden">
                                    <div className="flex flex-col items-center justify-center w-full h-full">
                                        <div className="rounded-full bg-blue-200/60 w-24 h-24 flex items-center justify-center mb-2 shadow-md">
                                            <span className="text-5xl text-blue-400 font-bold">🎫</span>
                                        </div>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="pt-4 px-6 pb-0">
                                <div className="flex flex-col gap-2">
                                    <CardTitle className="text-2xl font-bold text-blue-900 mb-1">{event.title}</CardTitle>
                                    <ScrollableDescription className="mb-4">
                                        <CardDescription className="text-base text-gray-700 mb-2">{event.description}</CardDescription>
                                    </ScrollableDescription>
                                    <div className="flex flex-wrap gap-2 text-sm text-blue-800 mb-2">
                                        <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200">
                                            {(() => {
                                                const [year, month, day] = event.date.split('T')[0].split('-');
                                                const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                                return date.toLocaleDateString('en-US', {
                                                    weekday: 'long',
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                });
                                            })()}
                                        </Badge>
                                        <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200">
                                            {new Date(`2000-01-01T${event.time_start}`).toLocaleTimeString('en-US', { 
                                                hour: 'numeric',
                                                minute: '2-digit',
                                                hour12: true 
                                            })} - {new Date(`2000-01-01T${event.time_end}`).toLocaleTimeString('en-US', { 
                                                hour: 'numeric',
                                                minute: '2-digit',
                                                hour12: true 
                                            })}
                                        </Badge>
                                        <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200">{event.location}</Badge>
                                    </div>
                                    <div className="flex flex-col gap-1 text-sm text-gray-500">
                                        <div className="flex items-center gap-2">
                                            <span className="whitespace-nowrap">Contact Email:</span>
                                            <a 
                                                href={`mailto:${event.contact_email}`} 
                                                className="text-blue-600 hover:text-teal-600 font-medium underline underline-offset-2 truncate"
                                                title={event.contact_email}
                                            >
                                                {event.contact_email}
                                            </a>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="whitespace-nowrap">Contact Phone:</span>
                                            <a 
                                                href={`tel:${event.contact_number}`} 
                                                className="text-blue-600 hover:text-teal-600 font-medium underline underline-offset-2 truncate"
                                                title={event.contact_number}
                                            >
                                                {event.contact_number}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                            <CardFooter className="flex flex-col items-center w-full pt-0">
                                <Separator className="my-4 w-full" />
                                <div className="flex items-center justify-between w-full px-6">
                                    <div className="flex items-center space-x-2">
                                        <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-0">
                                            {eventTicketsLeft} tickets left
                                        </Badge>
                                    </div>
                                    <a
                                        href={`mailto:${event.contact_email}`}
                                        className="text-sm text-blue-600 hover:text-teal-600 font-semibold"
                                    >
                                        Contact Support
                                    </a>
                                </div>
                                <div className="flex flex-col items-center mt-4 text-xs text-gray-400 w-full">
                                    <img src="/branding/TriEvents_Logo.png" alt="TriEvents" className="h-6 mb-1" />
                                    <span>Secure checkout powered by Stripe</span>
                                </div>
                            </CardFooter>
                        </Card>
                        {/* Platform Support Section - Hidden on mobile */}
                        <div className="hidden lg:flex mt-12 flex-col items-center gap-6 text-center text-gray-500">
                            <div className="flex items-center gap-3">
                                <div className="h-px w-24 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                                <span className="text-base font-medium">Platform Support</span>
                                <div className="h-px w-24 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                            </div>
                            <div className="flex flex-col items-center gap-3 text-sm">
                                <p className="text-gray-600">Having trouble with our platform?</p>
                                <p className="text-gray-500">Contact Tricode Cloud Support</p>
                                <a href="mailto:support@tricode.cloud" className="text-blue-600 hover:text-teal-600 transition-colors">
                                    support@tricode.cloud
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Registration Form */}
                    <div className="flex flex-col">
                        <Card className="w-full shadow-lg border-0">
                            <CardHeader className="mb-0">
                                <CardTitle className="text-blue-900">Event Registration</CardTitle>
                                <CardDescription>
                                    Please enter the name and email of the person attending the event.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="pt-0">
                                {/* Ticket Selection */}
                                <div className="space-y-4 mt-0">
                                    <Label className="text-blue-800">Select Ticket Type</Label>
                                    <div className="space-y-3">
                                        {prices.map((price, idx) => (
                                            <Card
                                                key={price.stripe_price_id}
                                                className={cn(
                                                    "transition-all border-2 border-transparent bg-blue-50/40 shadow-sm hover:shadow-md hover:bg-blue-100/60 cursor-pointer",
                                                    selectedOption === price.stripe_price_id && "border-blue-500 bg-blue-100/80 shadow-md",
                                                    price.tickets_available === 0 && "cursor-not-allowed hover:shadow-sm hover:bg-blue-50/40",
                                                    idx === prices.length - 1 && "mb-4"
                                                )}
                                                onClick={() => price.tickets_available > 0 && handleOptionChange(price.stripe_price_id)}
                                            >
                                                <CardContent className="p-4">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center space-x-3">
                                                            <Circle
                                                                className={cn(
                                                                    "h-5 w-5 flex-shrink-0",
                                                                    selectedOption === price.stripe_price_id ? "fill-blue-600 text-blue-600" : "text-gray-400",
                                                                    price.tickets_available === 0 && "text-gray-300"
                                                                )}
                                                            />
                                                            <div>
                                                                <p className={cn(
                                                                    "font-medium text-blue-900",
                                                                    price.tickets_available === 0 && "line-through text-gray-600"
                                                                )}>
                                                                    {price.nickname}
                                                                </p>
                                                                <p className="text-sm text-gray-500">{price.description}</p>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className={cn(
                                                                "font-bold text-blue-800",
                                                                price.tickets_available === 0 && "text-gray-600"
                                                            )}>
                                                                ${(price.unit_amount / 100).toFixed(2)}
                                                            </p>
                                                            {price.tickets_available > 0 ? (
                                                                <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-0 whitespace-nowrap">
                                                                    {price.tickets_available} available
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="destructive" className="bg-red-100 text-red-600 border-0 font-medium whitespace-nowrap">
                                                                    Sold Out
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-8 mb-6">
                                    <Separator />
                                </div>

                                {/* Registration Form */}
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="name" className="text-blue-800">Name</Label>
                                        <Input
                                            id="name"
                                            placeholder="Alan Turing"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="email" className="text-blue-800">Email</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="alan@bletchleypark.com"
                                            value={email}
                                            onChange={handleEmailChange}
                                            className={cn(emailError && "border-red-500")}
                                        />
                                        {emailError && <p className="text-sm text-red-500">{emailError}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="phone" className="text-blue-800">Phone Number</Label>
                                        <Input
                                            id="phone"
                                            type="tel"
                                            placeholder="441-908-6404"
                                            value={phone}
                                            onChange={handlePhoneChange}
                                            className={cn(phoneError && "border-red-500")}
                                            required
                                        />
                                        {phoneError && <p className="text-sm text-red-500">{phoneError}</p>}
                                    </div>

                                    {/* Bursary Section */}
                                    {event.bursary_enabled && (
                                        <>
                                            <Separator />
                                            <div className="space-y-4">
                                                    <div className="flex items-center space-x-2">
                                                        <Checkbox
                                                            id="bursary"
                                                            checked={requestBursary}
                                                            onCheckedChange={(checked) => {
                                                                setRequestBursary(checked as boolean);
                                                                if (!checked) {
                                                                    resetBursaryState();
                                                                }
                                                            }}
                                                            className="border border-blue-300 bg-white ring-1 ring-blue-200 focus:ring-2 focus:ring-blue-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-500 data-[state=checked]:ring-blue-400"
                                                        />
                                                        <Label htmlFor="bursary" className="text-blue-800 select-none cursor-pointer">Request financial assistance (bursary)</Label>
                                                    </div>

                                                {requestBursary && (
                                                    <div className="space-y-4">
                                                        <Alert className="bg-blue-50 border-blue-200 text-blue-800">
                                                            <AlertDescription>
                                                                <strong>Important:</strong> You will pay the full ticket price and will be refunded the bursary amount if your request is accepted. If you have any questions, please contact the event organizer.
                                                            </AlertDescription>
                                                        </Alert>
                                                        
                                                        {event.bursary_type === 'fixed' && event.bursary_options && (
                                                            <div className="space-y-2">
                                                                <Label className="text-blue-800" htmlFor="bursaryOptionSelect">Select Bursary Option</Label>
                                                                <select
                                                                    id="bursaryOptionSelect"
                                                                    className="block w-full rounded-lg border border-blue-300 px-4 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500"
                                                                    value={selectedBursaryOption?.amount.toString() || ''}
                                                                    onChange={e => {
                                                                        const value = e.target.value;
                                                                        if (!value) {
                                                                            resetBursaryState();
                                                                            return;
                                                                        }
                                                                        const option = event.bursary_options?.find(opt => opt.amount.toString() === value);
                                                                        if (option) {
                                                                            handleBursaryOptionSelect(option);
                                                                        }
                                                                    }}
                                                                >
                                                                    <option value="">Select an option</option>
                                                                    {event.bursary_options.map((option, index) => {
                                                                        const selectedPrice = prices.find(p => p.stripe_price_id === selectedOption);
                                                                        const ticketPrice = selectedPrice ? selectedPrice.unit_amount / 100 : null;
                                                                        // Only disable if a ticket is selected and the bursary exceeds the ticket price
                                                                        const isDisabled = ticketPrice !== null && option.amount > ticketPrice;
                                                                        return (
                                                                            <option
                                                                                key={index}
                                                                                value={option.amount}
                                                                                disabled={isDisabled}
                                                                            >
                                                                                ${option.amount} - {option.description}
                                                                                {isDisabled ? ` (Exceeds ticket price of $${ticketPrice})` : ''}
                                                                            </option>
                                                                        );
                                                                    })}
                                                                </select>
                                                            </div>
                                                        )}

                                                        {event.bursary_type === 'custom' && (
                                                            <div className="space-y-2">
                                                                <Label htmlFor="bursaryAmount" className="text-blue-800">Bursary Amount (CAD)</Label>
                                                                <Input
                                                                    id="bursaryAmount"
                                                                    type="number"
                                                                    placeholder="0.00"
                                                                    value={bursaryAmount}
                                                                    onChange={handleBursaryAmountChange}
                                                                    min="0"
                                                                    step="0.01"
                                                                    className={cn(bursaryError && "border-red-500")}
                                                                />
                                                            </div>
                                                        )}

                                                        <div className="space-y-2">
                                                            <Label htmlFor="bursaryMessage" className="text-blue-800">Why do you need a bursary?</Label>
                                                            <Textarea
                                                                id="bursaryMessage"
                                                                placeholder="Please explain why you need a bursary..."
                                                                value={bursaryMessage}
                                                                onChange={handleBursaryMessageChange}
                                                                className="h-24 resize-none"
                                                                maxLength={500}
                                                            />
                                                            <div className="flex justify-between items-center mt-1">
                                                                <p className="text-muted-foreground text-sm">
                                                                    {500 - bursaryMessage.length} characters remaining
                                                                </p>
                                                            </div>
                                                        </div>

                                                        {bursaryError && (
                                                            <p className="text-sm text-red-500">{bursaryError}</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </CardContent>
                            <CardFooter>
                                <div className="w-full space-y-3">
                                    
                                    
                                    <Button
                                        className="w-full bg-[#23d6e4] hover:bg-[#11c4d1] text-white text-lg font-semibold shadow-md border-0"
                                        size="lg"
                                        onClick={handleCheckout}
                                        disabled={!selectedOption || !name || !email || !phone || !!emailError || !!phoneError || (requestBursary && (!bursaryAmount || !bursaryMessage || !!bursaryError))}
                                    >
                                        Continue to Payment
                                    </Button>
                                </div>
                            </CardFooter>
                        </Card>
                    </div>
                </div>

                {/* Platform Support Section - Only visible on mobile */}
                <div className="lg:hidden mt-12 flex flex-col items-center gap-6 text-center text-gray-500">
                    <div className="flex items-center gap-3">
                        <div className="h-px w-24 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                        <span className="text-base font-medium">Platform Support</span>
                        <div className="h-px w-24 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                    </div>
                    <div className="flex flex-col items-center gap-3 text-sm">
                        <p className="text-gray-600">Having trouble with our platform?</p>
                        <p className="text-gray-500">Contact Tricode Cloud Support</p>
                        <a href="mailto:support@tricode.cloud" className="text-blue-600 hover:text-teal-600 transition-colors">
                            support@tricode.cloud
                        </a>
                    </div>
                </div>
            </div>
            <div className="mt-16 md:mt-24">
                <Footer />
            </div>

            {/* Terms and Privacy Policy Dialog */}
            <Dialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Terms of Service & Privacy Policy</DialogTitle>
                        <DialogDescription>
                            Before proceeding to payment, please review and agree to our terms.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="text-sm text-gray-600 space-y-2">
                            <p>
                                By proceeding with this purchase, you agree to our{" "}
                                <a 
                                    href="https://tricode.cloud/trievents/terms-of-service" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline"
                                >
                                    Terms of Service
                                </a>{" "}
                                and{" "}
                                <a 
                                    href="https://tricode.cloud/trievents/privacy-policy" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline"
                                >
                                    Privacy Policy
                                </a>.
                            </p>
                            <p>
                                Your personal information will be used to process your ticket purchase and may be shared with the event organizer for event management purposes.
                            </p>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="agree-terms"
                                checked={agreedToTerms}
                                onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
                                className="h-5 w-5 border-2 data-[state=checked]:bg-black data-[state=checked]:border-black"
                            />
                            <Label htmlFor="agree-terms" className="text-sm">
                                I agree to the Terms of Service and Privacy Policy
                            </Label>
                        </div>
                    </div>
                    <DialogFooter className="flex flex-col sm:flex-row gap-3 sm:gap-0">
                        <Button 
                            onClick={handleProceedToPayment}
                            disabled={!agreedToTerms}
                            className="w-full sm:w-auto transition-transform duration-200 hover:scale-105"
                        >
                            Proceed to Payment
                        </Button>
                        <Button 
                            variant="outline" 
                            onClick={() => setShowTermsDialog(false)}
                            className="w-full sm:w-auto transition-transform duration-200 hover:scale-105"
                        >
                            Cancel
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CheckoutPage;
